import { useEffect, useState } from 'react';
import {
  Huddle,
  HuddleGitHubTopTen,
  HuddleOrgDashboard,
  HuddleOrgNotes,
  OrgId,
} from '../../../../../common/src/model.ts';
import * as server_api from '../../../../../common/src/server_api.ts';

// Fetch-on-mount, in the shape of useAllOrgs.  The request is a pure read - the rotation advances on
// a server job, weekdays at 2 AM Mountain - so a reload is free and the weekend shows Friday's.
export function useHuddle(): { huddle: Huddle | undefined; isLoading: boolean; error: string | undefined } {
  const [huddle, setHuddle] = useState<Huddle | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    void (async () => {
      try {
        setHuddle(await server_api.getHuddle());
      } catch (e: any) {
        setError(e.message);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  return { huddle, isLoading, error };
}

// Deliberately a separate request from useHuddle: GitHub can be slow or down, and the prayer focus
// cards must render regardless.  The server never throws here, so `error` covers only the request
// itself failing; an absent list arrives as a `notice` on a successful response.
export function useHuddleGitHubTopTen(): {
  topTen: HuddleGitHubTopTen | undefined;
  isLoading: boolean;
  error: string | undefined;
} {
  const [topTen, setTopTen] = useState<HuddleGitHubTopTen | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    void (async () => {
      try {
        setTopTen(await server_api.getHuddleGitHubTopTen());
      } catch (e: any) {
        setError(e.message);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  return { topTen, isLoading, error };
}

// The dashboard embedded under the active church card.  Also its own request, for the same reason
// as the two above: it is the heaviest query on the page (every household and every member of the
// org), and the cards must be readable long before it lands.
export function useHuddleOrgDashboard(orgId: OrgId): {
  dashboard: HuddleOrgDashboard | undefined;
  isLoading: boolean;
  error: string | undefined;
} {
  const [dashboard, setDashboard] = useState<HuddleOrgDashboard | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    void (async () => {
      try {
        setDashboard(await server_api.getHuddleOrgDashboard(orgId));
      } catch (e: any) {
        setError(e.message);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [orgId]);

  return { dashboard, isLoading, error };
}

// One request per card, independent of the huddle itself: Google Drive must never hold up - or
// break - the prayer focus cards.  With no credential configured the server returns {}, which the
// card renders as nothing at all.
export function useHuddleOrgNotes(orgId: OrgId): {
  notes: HuddleOrgNotes | undefined;
  isLoading: boolean;
  error: string | undefined;
} {
  const [notes, setNotes] = useState<HuddleOrgNotes | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    void (async () => {
      try {
        setNotes(await server_api.getHuddleOrgNotes(orgId));
      } catch (e: any) {
        setError(e.message);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [orgId]);

  return { notes, isLoading, error };
}
